import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { RiArrowDownSLine, RiArrowLeftDoubleFill, RiArrowRightDoubleFill } from 'react-icons/ri'
import Seo from './Seo'
import ChampionIconWithFallback from '../shared/ChampionIcon'
import ProfileIconWithFallback from '../shared/ProfileIcon'
import { assetUrl, axiosForAPI } from '../helper_functions/utilities'
import { DISPLAY_REGION_TO_PLATFORM, formatPlatformLabel, PLATFORM_OPTIONS } from '../helper_functions/searchRouting'
import { scopedPlatformOptions, useDeploymentCapabilities } from '../context/DeploymentCapabilitiesContext'
import { buildSearchTierIconSrc, buildSearchRoleIconSrc, formatSearchMasteryPoints } from './search/searchHelpers'

const TIER_OPTIONS = [
    { value: 'all', label: 'ALL TIERS' },
    { value: 'emerald_plus', label: 'EMERALD+' },
    { value: 'diamond_plus', label: 'DIAMOND+' },
    { value: 'master_plus', label: 'MASTER+' },
    { value: 'grandmaster_plus', label: 'GRANDMASTER+' },
    { value: 'iron', label: 'IRON' },
    { value: 'bronze', label: 'BRONZE' },
    { value: 'silver', label: 'SILVER' },
    { value: 'gold', label: 'GOLD' },
    { value: 'platinum', label: 'PLATINUM' },
    { value: 'emerald', label: 'EMERALD' },
    { value: 'diamond', label: 'DIAMOND' },
    { value: 'master', label: 'MASTER' },
    { value: 'grandmaster', label: 'GRANDMASTER' },
    { value: 'challenger', label: 'CHALLENGER' },
]

const PLATFORM_TO_DISPLAY_REGION = Object.fromEntries(
    Object.entries(DISPLAY_REGION_TO_PLATFORM).map(([displayRegion, platform]) => [platform, displayRegion])
)
const APEX_TIERS = new Set(['MASTER', 'GRANDMASTER', 'CHALLENGER'])
const EXCLUDED_MASTERY_CHAMPIONS = new Set(['rhaast', 'shadowassassin'])

function MasteryLeaderboard() {
    const deploymentCapabilities = useDeploymentCapabilities()
    const scopedOptions = scopedPlatformOptions(PLATFORM_OPTIONS, deploymentCapabilities)
    const navigate = useNavigate()
    const { championNameId: routeChampionNameId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const [champions, setChampions] = useState([])
    const [leaderboard, setLeaderboard] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const championScrollerRef = useRef(null)

    const championNameId = routeChampionNameId || searchParams.get('champion') || 'aatrox'
    const platform = searchParams.get('platform') || ''
    const tier = searchParams.get('tier') || 'all'
    const page = Math.max(1, Number(searchParams.get('page') || 1))

    useEffect(() => {
        const controller = new AbortController()
        axiosForAPI.get('/champions/', { signal: controller.signal })
            .then((response) => setChampions(response.data || []))
            .catch((requestError) => {
                if (!axios.isCancel(requestError) && requestError?.code !== 'ERR_CANCELED') {
                    setError('Unable to load champions right now.')
                }
            })
        return () => controller.abort()
    }, [])

    useEffect(() => {
        const controller = new AbortController()
        setLoading(true)
        setError('')

        axiosForAPI.get('/leaderboards/mastery/', {
            params: { champion: championNameId, platform: platform || undefined, tier, page, page_size: 25 },
            signal: controller.signal,
        })
            .then((response) => setLeaderboard(response.data))
            .catch((requestError) => {
                if (axios.isCancel(requestError) || requestError?.code === 'ERR_CANCELED') return
                setLeaderboard(null)
                setError(requestError?.response?.data?.detail || 'Unable to load the mastery leaderboard right now.')
            })
            .finally(() => setLoading(false))

        return () => controller.abort()
    }, [championNameId, page, platform, tier])

    const selectedChampion = useMemo(
        () => champions.find((champion) => champion.name_id === championNameId) || leaderboard?.champion,
        [champions, championNameId, leaderboard]
    )
    const visibleChampions = useMemo(
        () => champions
            .filter((champion) => champion.champion_id !== -1 && champion.name?.toLowerCase() !== 'unknown' && champion.name_id && !EXCLUDED_MASTERY_CHAMPIONS.has(champion.name_id.toLowerCase()))
            .sort((left, right) => left.name.localeCompare(right.name)),
        [champions]
    )

    useEffect(() => {
        if (!visibleChampions.length) return undefined
        const frame = window.requestAnimationFrame(() => {
            championScrollerRef.current
                ?.querySelector('[data-selected-champion="true"]')
                ?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' })
        })
        return () => window.cancelAnimationFrame(frame)
    }, [championNameId, visibleChampions.length])

    function updateFilters(changes, resetPage = true) {
        const next = new URLSearchParams(searchParams)
        Object.entries(changes).forEach(([key, value]) => {
            if (key === 'champion' && routeChampionNameId) return
            if (value) next.set(key, value)
            else next.delete(key)
        })
        if (resetPage) next.delete('page')
        if (changes.champion && routeChampionNameId) {
            next.delete('champion')
            const query = next.toString()
            navigate(`/analytics/mastery/${encodeURIComponent(changes.champion)}${query ? `?${query}` : ''}`)
        } else {
            setSearchParams(next)
        }
    }

    function profilePath(result) {
        const displayRegion = PLATFORM_TO_DISPLAY_REGION[result.platform_code] || 'na'
        return `/summoners/${displayRegion}/${encodeURIComponent(result.game_name)}-${encodeURIComponent(result.tag_line)}`
    }

    const championLabel = selectedChampion?.name || leaderboard?.champion?.name || (championNameId === 'aatrox' ? 'Aatrox' : 'Champion')
    const currentPage = leaderboard?.page || page
    const totalPages = leaderboard?.total_pages || 0

    function renderPageButtons() {
        const pageButtons = []
        for (let pageNumber = Math.max(1, currentPage - 1); pageNumber <= Math.min(totalPages, currentPage + 3); pageNumber += 1) {
            pageButtons.push(pageNumber)
        }

        return (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                {currentPage > 1 ? (
                    <button
                        type="button"
                        title="First page"
                        aria-label="Go to first page"
                        onClick={() => updateFilters({ page: '1' }, false)}
                        className="inline-flex h-8 min-w-8 items-center justify-center rounded-md border border-zinc-700 bg-zinc-900 px-2 text-zinc-300 transition hover:border-zinc-600 hover:bg-zinc-800 hover:text-zinc-100"
                    >
                        <RiArrowLeftDoubleFill className="h-4 w-4" />
                    </button>
                ) : null}
                {pageButtons.map((pageNumber) => (
                    <button
                        key={pageNumber}
                        type="button"
                        aria-current={pageNumber === currentPage ? 'page' : undefined}
                        onClick={() => updateFilters({ page: String(pageNumber) }, false)}
                        className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-3 text-[11px] font-semibold tabular-nums transition ${pageNumber === currentPage
                            ? 'border-cyan-700/70 bg-cyan-950/55 text-cyan-100 hover:border-cyan-600/80 hover:bg-cyan-950/70'
                            : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-600 hover:bg-zinc-800 hover:text-zinc-100'
                        }`}
                    >
                        {pageNumber}
                    </button>
                ))}
                {currentPage < totalPages ? (
                    <button
                        type="button"
                        title="Last page"
                        aria-label="Go to last page"
                        onClick={() => updateFilters({ page: String(totalPages) }, false)}
                        className="inline-flex h-8 min-w-8 items-center justify-center rounded-md border border-zinc-700 bg-zinc-900 px-2 text-zinc-300 transition hover:border-zinc-600 hover:bg-zinc-800 hover:text-zinc-100"
                    >
                        <RiArrowRightDoubleFill className="h-4 w-4" />
                    </button>
                ) : null}
            </div>
        )
    }

    return (
        <div className="mx-auto w-full max-w-screen-lg text-zinc-200">
            <div className="mx-4 pt-6 pb-8 custom-lg:mx-0 sm:pt-8 sm:pb-10">
                <Seo
                    title={`${championLabel} Mastery Leaderboard | WINRATES.GG™`}
                    description={`Browse the highest mastery ${championLabel} players with tier and region filters on WINRATES.GG™.`}
                    canonical={`/analytics/mastery/${encodeURIComponent(championNameId)}`}
                />
                <header className="mb-5">
                    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-400">Mastery leaderboard</div>
                        <Link
                            className="inline-flex text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 transition hover:text-cyan-300"
                            to={`/analytics/champions/${encodeURIComponent(selectedChampion?.name_id || championNameId)}`}
                        >
                            {championLabel} Analytics
                        </Link>
                    </div>
                    <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-100 sm:text-3xl">{championLabel} Players</h1>
                    <p className="mt-2 max-w-2xl text-sm text-zinc-400">Champion mastery leaders, ranked by mastery points.</p>
                </header>

                <section className="rounded-lg border border-zinc-800 bg-zinc-950 p-3 sm:p-4">
                    <div className="flex items-end justify-between gap-3">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">Champion</div>
                            <div className="mt-1 text-sm font-semibold text-zinc-100">{championLabel}</div>
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-600">Select a champion</div>
                    </div>
                    <div ref={championScrollerRef} className="mt-3 -mx-1 grid auto-cols-[56px] grid-flow-col grid-rows-3 gap-2 overflow-x-auto px-1 py-1">
                        {visibleChampions.map((champion) => {
                            const isSelected = champion.name_id.toLowerCase() === championNameId.toLowerCase()
                            return (
                                <button
                                    key={champion.champion_id}
                                    type="button"
                                    title={champion.name}
                                    aria-label={`View ${champion.name} mastery players`}
                                    aria-pressed={isSelected}
                                    data-selected-champion={isSelected ? 'true' : undefined}
                                    onClick={() => updateFilters({ champion: champion.name_id })}
                                    className={`group flex w-14 shrink-0 flex-col items-center gap-1.5 rounded-md px-1.5 py-1.5 transition ${isSelected ? 'bg-cyan-900/45 text-cyan-200' : 'text-zinc-500 hover:bg-zinc-900 hover:text-zinc-200'}`}
                                >
                                    <span className="h-10 w-10 overflow-hidden rounded-full border border-zinc-700 bg-zinc-900">
                                        <ChampionIconWithFallback
                                            alt=""
                                            className="h-full w-full scale-[1.18] object-cover"
                                            src={assetUrl(`/champion_icons/${champion.name_id}.png`)}
                                        />
                                    </span>
                                    <span className="w-full truncate text-[9px] font-semibold uppercase tracking-[0.04em]">{champion.name}</span>
                                </button>
                            )
                        })}
                    </div>
                </section>

                <section className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:p-0">
                    <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">
                        Tier
                        <div className="relative mt-1.5">
                            <select className="w-full appearance-none rounded-md border border-zinc-700 bg-zinc-900 px-2 py-2 pr-9 text-xs font-semibold text-zinc-200 focus:border-cyan-700/70 focus:outline-none focus:ring-1 focus:ring-cyan-700/45" value={tier} onChange={(event) => updateFilters({ tier: event.target.value })}>
                                {TIER_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </select>
                            <RiArrowDownSLine aria-hidden="true" className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                        </div>
                    </label>
                    <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">
                        Region
                        <div className="relative mt-1.5">
                            <select className="w-full appearance-none rounded-md border border-zinc-700 bg-zinc-900 px-2 py-2 pr-9 text-xs font-semibold text-zinc-200 focus:border-cyan-700/70 focus:outline-none focus:ring-1 focus:ring-cyan-700/45" value={platform} onChange={(event) => updateFilters({ platform: event.target.value })}>
                                {scopedOptions.map((option) => <option key={option.value || 'global'} value={option.value} disabled={option.disabled}>{option.label}</option>)}
                            </select>
                            <RiArrowDownSLine aria-hidden="true" className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                        </div>
                    </label>
                </section>

                <div className="mt-4 flex items-center justify-between text-[11px] text-zinc-400">
                    <span>{leaderboard && !loading ? `${leaderboard.total_results.toLocaleString()} players considered` : 'Loading leaderboard...'}</span>
                    <span>{totalPages ? `Page ${currentPage} of ${totalPages}` : ''}</span>
                </div>

                {error ? <div className="mt-3 rounded-lg border border-rose-900/60 bg-rose-950/20 px-4 py-3 text-sm text-rose-200">{error}</div> : null}
                {loading ? <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-5 text-sm text-zinc-400">Loading mastery leaders...</div> : null}
                {!loading && !error && leaderboard?.results?.length ? (
                    <div className="mt-3 space-y-2">
                        {leaderboard.results.map((result) => {
                            const tierLabel = result.tier ? `${result.tier}${APEX_TIERS.has(result.tier) ? '' : (result.division ? ` ${result.division}` : '')}` : 'UNRANKED'
                            const isTopRank = result.rank === 1
                            return (
                                <Link key={`${result.puuid}-${result.platform_code}`} to={profilePath(result)} className={`grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 rounded-lg border px-3 py-3 transition sm:grid-cols-[minmax(0,1.55fr)_150px_120px_150px] ${isTopRank ? 'border-cyan-700/70 bg-cyan-900/25 shadow-[inset_0_1px_0_rgba(103,232,249,0.3),inset_0_-1px_0_rgba(34,211,238,0.18)] hover:border-cyan-600/80 hover:bg-cyan-900/35' : 'border-zinc-800 bg-zinc-950 hover:border-cyan-800/70 hover:bg-cyan-950/15'}`}>
                                    <div className="flex min-w-0 items-center gap-3">
                                        <div className="w-7 shrink-0 text-center text-sm font-semibold tabular-nums text-zinc-500">{result.rank}</div>
                                        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-zinc-700"><ProfileIconWithFallback className="block h-full w-full object-cover" src={assetUrl(`/profile_icons/${result.profile_icon_id}.png`)} alt="" /></div>
                                        <div className="min-w-0"><div className="truncate text-sm font-semibold text-zinc-100">{result.game_name}</div><div className="truncate text-[11px] uppercase tracking-[0.1em] text-zinc-400">#{result.tag_line} · {formatPlatformLabel(result.platform_code)}</div></div>
                                    </div>
                                    <div className="hidden min-w-0 items-center gap-2 sm:flex"><img className="h-9 w-9 object-contain" src={buildSearchTierIconSrc(result.tier)} alt="" /><div><div className="text-[12px] font-semibold text-zinc-200">{tierLabel}</div><div className="text-[11px] text-zinc-400">{result.league_points} LP</div></div></div>
                                    <div className="flex items-center justify-center gap-2 text-[12px] font-medium uppercase tracking-[0.1em] text-zinc-300 sm:justify-start">
                                        {result.preferred_role && buildSearchRoleIconSrc(result.preferred_role) ? (
                                            <img className="h-6 w-6 shrink-0 opacity-90" src={buildSearchRoleIconSrc(result.preferred_role)} alt={`${result.preferred_role} role icon`} />
                                        ) : null}
                                        <span className="hidden sm:inline">{result.preferred_role || '—'}</span>
                                    </div>
                                    <div className="text-right"><div className="text-sm font-semibold text-cyan-200">{formatSearchMasteryPoints(result.mastery_points)}</div><div className="text-[11px] uppercase tracking-[0.1em] text-zinc-400">Level {result.mastery_level}</div></div>
                                </Link>
                            )
                        })}
                    </div>
                ) : null}
                {!loading && !error && !leaderboard?.results?.length ? <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-5 text-sm text-zinc-400">No observed mastery players match these filters.</div> : null}

                {totalPages > 1 ? renderPageButtons() : null}
            </div>
        </div>
    )
}

export default MasteryLeaderboard
